/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable camelcase */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Edit,
  Phone,
  NotificationsActive,
  Sms,
  Email,
  ReceiptLong,
  Smartphone,
  Computer,
  CheckCircle,
  Cancel,
  Devices,
  Key,
  Campaign,
  AccountBalanceWallet,
  ArrowForwardIos,
  Person,
  VerifiedUser,
  Star,
  AccessTime,
  CalendarToday,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getUserById, queryUserData } from '../api/users.api';
import Page from '../components/Page';
import BasicModal from './components/Modal';
import UserOptionsCardMessage from './components/User/UserOptionsCardMessage';
import UserOptionsCardPushNotification from './components/User/UserOptionsCardPushNotification';
import UserOptionsCardEmail from './components/User/UserOptionsCardEmail';
import UserOptionsCardInfo from './components/User/UserOptionsCardInfo';
import UserOptionsCardDeduct from './components/User/UserOptionsCardDeduct';
import UserOptionsCardFund from './components/User/UserOptionsCardFund';
import { fCurrency } from '../utils/formatNumber';
import { hasPermission, PERMISSIONS } from '../utils/hasPermission';

// ─── Design Tokens ──────────────────────────────────────────────────────────
// Surface / text
const INK = '#1D1D1F';
const INK_SOFT = '#6E6E73';
const INK_FAINT = '#86868B';
const HAIRLINE = 'rgba(0,0,0,0.06)';
const SURFACE = '#FFFFFF';
const CANVAS = '#F5F5F7';
// Semantic accents
const BLUE = '#0071E3';
const GREEN = '#34C759';
const RED = '#FF3B30';
const ORANGE = '#FF9500';
const PURPLE = '#AF52DE';

const FONT_STACK = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Segoe UI", Roboto, sans-serif';

// ─── Styled Primitives ──────────────────────────────────────────────────────

const Surface = styled(Paper)(({ theme }) => ({
  borderRadius: 22,
  background: SURFACE,
  border: `1px solid ${HAIRLINE}`,
  boxShadow: '0 1px 2px rgba(0,0,0,0.02), 0 10px 30px rgba(0,0,0,0.03)',
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2.25),
    borderRadius: 18,
  },
}));

const SectionLabel = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 10,
});

const IconChip = styled(Box)(({ tint }) => ({
  width: 34,
  height: 34,
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `${tint}17`,
  color: tint,
  flexShrink: 0,
}));

const Tile = styled(Box)({
  padding: '14px 16px',
  borderRadius: 16,
  background: 'rgba(0,0,0,0.025)',
  border: '1px solid rgba(0,0,0,0.03)',
});

const StatTile = styled(Box)(({ tint }) => ({
  padding: '18px 18px',
  borderRadius: 18,
  background: `${tint}0C`,
  border: `1px solid ${tint}1F`,
  height: '100%',
}));

const QuickAction = styled(Box)(({ tint, disabled }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 6,
  padding: '12px 6px',
  borderRadius: 16,
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.35 : 1,
  transition: 'background-color 0.2s ease, transform 0.15s ease',
  '&:hover': disabled ? {} : { backgroundColor: `${tint}0F`, transform: 'translateY(-1px)' },
}));

const QuickActionIcon = styled(Box)(({ tint }) => ({
  width: 46,
  height: 46,
  borderRadius: 15,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `${tint}17`,
  color: tint,
}));

const StatusPill = styled(Chip)({
  fontWeight: 700,
  fontSize: '0.68rem',
  height: 24,
  borderRadius: 8,
  letterSpacing: 0.2,
});

export default function UserDetails() {
  const navigate = useNavigate();
  const { id: paramUserId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const searchUserId = queryParams.get('show') || queryParams.get('id');

  const userId = paramUserId || searchUserId || '';

  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [toggleFundUser, setToggleFundUser] = useState(false);
  const [toggleDeductUser, setToggleDeductUser] = useState(false);

  const [togglePushNotification, setTogglePushNotification] = useState(false);
  const [toggleSendUserMessage, setToggleSendUserMessage] = useState(false);
  const [toggleSendUserEmail, setToggleSendUserEmail] = useState(false);

  const [walletActionType, setWalletActionType] = useState('');
  const [disableAction, setDisableAction] = useState(false);
  const [modifyUser, setModifyUser] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);

  const { user } = useSelector((state) => state.auth);

  const fetchUserDataById = useCallback(async (targetId) => {
    if (!targetId) return;
    setLoading(true);
    let response = await getUserById(targetId);

    if (!response.ok || !response.data?.success) {
      response = await queryUserData({ id: targetId });
    }

    if (response.ok && response.data?.success) {
      setSelectedUser(response.data?.data);
      setDisableAction(false);
    } else {
      toast.error(response.data?.message || 'User not found');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (userId) {
      fetchUserDataById(userId);
    }
  }, [userId, fetchUserDataById]);

  const getUserData = async () => {
    setDisableAction(true);
    fetchUserDataById(userId);
  };

  const navigateUserTransactionScreen = () => navigate(`/dashboard/transaction?show=${selectedUser?.user?.id}`);

  const handleCallUser = () => {
    if (selectedUser?.user?.phone) {
      window.open(`tel:${selectedUser.user.phone}`, '_blank');
    }
  };

  const toggleAddFund = (type) => {
    setWalletActionType(type);
    setToggleFundUser(!toggleFundUser);
  };

  const toggleDeductFund = (type) => {
    setWalletActionType(type);
    setToggleDeductUser(!toggleDeductUser);
  };

  const toggleSendPush = () => {
    setTogglePushNotification(!togglePushNotification);
  };

  const toggleSendMessage = () => {
    setToggleSendUserMessage(!toggleSendUserMessage);
  };
  const toggleSendEmail = () => {
    setToggleSendUserEmail(!toggleSendUserEmail);
  };

  const toggleModifyUser = () => setModifyUser(!modifyUser);

  const handleUpdateUser = (data) => {
    setSelectedUser({ ...selectedUser, user: { ...selectedUser.user, ...data } });
    toggleModifyUser();
  };

  const userInfo = selectedUser?.user;
  const userWallet = selectedUser?.wallet;
  const activeSessions = Array.isArray(userInfo?.activeSessions) ? userInfo.activeSessions : [];

  const getSessionMeta = (session) => {
    const isMobileSession = session?.deviceType === 'mobile' || session?.entityType === 'mobile';
    const deviceLabel =
      [session?.deviceVendor, session?.deviceModel].filter(Boolean).join(' ') ||
      (isMobileSession ? 'Mobile Device' : 'Desktop Browser');
    const osLabel = [session?.osName, session?.osVersion].filter(Boolean).join(' ') || '—';
    const browserLabel = [session?.browserName, session?.browserVersion].filter(Boolean).join(' ') || 'Mobile App';
    return { isMobileSession, deviceLabel, osLabel, browserLabel };
  };

  const displayName =
    `${userInfo?.firstName || ''} ${userInfo?.lastName || ''}`.trim() || userInfo?.username || 'User Profile';
  const isVip = userInfo?.vip_status === true || userInfo?.vip_status === 'vip' || userInfo?.vip_status === 'true';
  const isKyc = userInfo?.isKycVerified === true || userInfo?.isKycVerified === 'true';
  const st = (userInfo?.status || 'active').toLowerCase();
  const hasPushToken = Boolean(userInfo?.deviceToken && userInfo.deviceToken.trim() !== '');

  const canMessage = hasPermission(user, PERMISSIONS.AGENTS_MESSAGE);
  const canFund = hasPermission(user, PERMISSIONS.AGENTS_FUND);

  const quickActions = [
    {
      key: 'call',
      label: 'Call',
      icon: Phone,
      tint: GREEN,
      disabled: !userInfo?.phone,
      onClick: handleCallUser,
      tooltip: 'Direct phone call',
    },
    {
      key: 'push',
      label: 'Push',
      icon: NotificationsActive,
      tint: PURPLE,
      disabled: !canMessage,
      onClick: toggleSendPush,
      tooltip: 'Send push notification',
    },
    {
      key: 'sms',
      label: 'SMS',
      icon: Sms,
      tint: BLUE,
      disabled: !canMessage || !userInfo?.phone,
      onClick: toggleSendMessage,
      tooltip: 'Send direct SMS',
    },
    {
      key: 'email',
      label: 'Email',
      icon: Email,
      tint: ORANGE,
      disabled: !canMessage,
      onClick: toggleSendEmail,
      tooltip: 'Send email message',
    },
    {
      key: 'edit',
      label: 'Edit',
      icon: Edit,
      tint: INK,
      disabled: false,
      onClick: toggleModifyUser,
      tooltip: 'Edit profile details',
    },
  ];

  return (
    <Page title={displayName}>
      {/* Modals */}
      <BasicModal isOpen={toggleFundUser} toggleOpen={toggleAddFund}>
        <UserOptionsCardFund
          user={selectedUser}
          closeModal={toggleAddFund}
          handleRefresh={getUserData}
          onSuccess={toggleAddFund}
          type={walletActionType}
        />
      </BasicModal>

      <BasicModal isOpen={toggleDeductUser} toggleOpen={toggleDeductFund}>
        <UserOptionsCardDeduct
          user={selectedUser}
          closeModal={toggleDeductFund}
          handleRefresh={getUserData}
          onSuccess={toggleDeductFund}
          type={walletActionType}
        />
      </BasicModal>

      <BasicModal isOpen={togglePushNotification} toggleOpen={toggleSendPush}>
        <UserOptionsCardPushNotification user={selectedUser} closeModal={toggleSendPush} />
      </BasicModal>

      <BasicModal isOpen={toggleSendUserMessage} toggleOpen={toggleSendMessage}>
        <UserOptionsCardMessage user={selectedUser} closeModal={toggleSendMessage} />
      </BasicModal>

      <BasicModal isOpen={toggleSendUserEmail} toggleOpen={toggleSendEmail}>
        <UserOptionsCardEmail user={selectedUser} closeModal={toggleSendEmail} />
      </BasicModal>

      <BasicModal isOpen={modifyUser} toggleOpen={toggleModifyUser}>
        <UserOptionsCardInfo user={selectedUser} closeModal={toggleModifyUser} onUserUpdate={handleUpdateUser} />
      </BasicModal>

      <BasicModal isOpen={Boolean(selectedSession)} toggleOpen={() => setSelectedSession(null)}>
        {selectedSession &&
          (() => {
            const { isMobileSession, deviceLabel, osLabel, browserLabel } = getSessionMeta(selectedSession);
            return (
              <Box sx={{ fontFamily: FONT_STACK, width: { xs: 'auto', sm: 340 }, maxWidth: '100%' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 14,
                      bgcolor: isMobileSession ? `${GREEN}1F` : `${BLUE}1F`,
                      color: isMobileSession ? GREEN : BLUE,
                    }}
                  >
                    {isMobileSession ? <Smartphone sx={{ fontSize: 22 }} /> : <Computer sx={{ fontSize: 22 }} />}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, color: INK, fontSize: '1.05rem', letterSpacing: '-0.01em' }}>
                      {deviceLabel}
                    </Typography>
                    <Typography sx={{ color: INK_FAINT, fontSize: '0.78rem', fontWeight: 500 }}>
                      Session details
                    </Typography>
                  </Box>
                </Stack>

                <Stack spacing={1.25}>
                  <Tile>
                    <Typography
                      sx={{
                        color: INK_FAINT,
                        fontWeight: 600,
                        display: 'block',
                        mb: 0.3,
                        fontSize: '0.68rem',
                        textTransform: 'uppercase',
                      }}
                    >
                      Operating System
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: INK, fontSize: '0.9rem' }}>{osLabel}</Typography>
                  </Tile>

                  <Tile>
                    <Typography
                      sx={{
                        color: INK_FAINT,
                        fontWeight: 600,
                        display: 'block',
                        mb: 0.3,
                        fontSize: '0.68rem',
                        textTransform: 'uppercase',
                      }}
                    >
                      App / Browser
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: INK, fontSize: '0.9rem' }}>{browserLabel}</Typography>
                  </Tile>

                  <Tile>
                    <Typography
                      sx={{
                        color: INK_FAINT,
                        fontWeight: 600,
                        display: 'block',
                        mb: 0.3,
                        fontSize: '0.68rem',
                        textTransform: 'uppercase',
                      }}
                    >
                      IP Address
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: BLUE, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {selectedSession.ipAddress || '—'}
                    </Typography>
                  </Tile>

                  <Tile>
                    <Typography
                      sx={{
                        color: INK_FAINT,
                        fontWeight: 600,
                        display: 'block',
                        mb: 0.3,
                        fontSize: '0.68rem',
                        textTransform: 'uppercase',
                      }}
                    >
                      Device ID
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: INK,
                        fontFamily: 'monospace',
                        fontSize: '0.78rem',
                        wordBreak: 'break-all',
                      }}
                    >
                      {selectedSession.deviceId || '—'}
                    </Typography>
                  </Tile>

                  <Tile>
                    <Typography
                      sx={{
                        color: INK_FAINT,
                        fontWeight: 600,
                        display: 'block',
                        mb: 0.3,
                        fontSize: '0.68rem',
                        textTransform: 'uppercase',
                      }}
                    >
                      Session ID
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: INK,
                        fontFamily: 'monospace',
                        fontSize: '0.78rem',
                        wordBreak: 'break-all',
                      }}
                    >
                      {selectedSession.sessionId || '—'}
                    </Typography>
                  </Tile>

                  <Tile>
                    <Typography
                      sx={{
                        color: INK_FAINT,
                        fontWeight: 600,
                        display: 'block',
                        mb: 0.3,
                        fontSize: '0.68rem',
                        textTransform: 'uppercase',
                      }}
                    >
                      Last Active
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: INK, fontSize: '0.9rem' }}>
                      {selectedSession.lastActiveAt
                        ? moment(selectedSession.lastActiveAt).format('DD MMM YYYY, hh:mm A')
                        : '—'}
                    </Typography>
                    <Typography sx={{ color: INK_FAINT, fontSize: '0.75rem' }}>
                      {selectedSession.lastActiveAt ? moment(selectedSession.lastActiveAt).fromNow() : ''}
                    </Typography>
                  </Tile>

                  <Grid container spacing={1.25}>
                    <Grid item xs={6}>
                      <Tile>
                        <Typography
                          sx={{
                            color: INK_FAINT,
                            fontWeight: 600,
                            display: 'block',
                            mb: 0.3,
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                          }}
                        >
                          Entity Type
                        </Typography>
                        <Typography
                          sx={{ fontWeight: 700, color: INK, fontSize: '0.82rem', textTransform: 'capitalize' }}
                        >
                          {selectedSession.entityType || '—'}
                        </Typography>
                      </Tile>
                    </Grid>
                    <Grid item xs={6}>
                      <Tile>
                        <Typography
                          sx={{
                            color: INK_FAINT,
                            fontWeight: 600,
                            display: 'block',
                            mb: 0.3,
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                          }}
                        >
                          Device Type
                        </Typography>
                        <Typography
                          sx={{ fontWeight: 700, color: INK, fontSize: '0.82rem', textTransform: 'capitalize' }}
                        >
                          {selectedSession.deviceType || '—'}
                        </Typography>
                      </Tile>
                    </Grid>
                  </Grid>
                </Stack>
              </Box>
            );
          })()}
      </BasicModal>

      <Box
        sx={{
          bgcolor: CANVAS,
          minHeight: '100vh',
          pb: 10,
          pt: { xs: 2, sm: 3.5 },
          fontFamily: FONT_STACK,
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3.5 } }}>
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: { xs: 2.5, sm: 3.5 } }}
          >
            <Stack direction="row" alignItems="center" spacing={1.75} sx={{ minWidth: 0 }}>
              <IconButton
                onClick={() => navigate('/dashboard/user-management')}
                sx={{
                  bgcolor: SURFACE,
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  border: `1px solid ${HAIRLINE}`,
                  flexShrink: 0,
                  transition: 'transform 0.2s ease',
                  '&:hover': { bgcolor: SURFACE, transform: 'scale(1.05)' },
                }}
              >
                <ArrowBackIcon sx={{ color: INK, fontSize: 19 }} />
              </IconButton>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: INK,
                    letterSpacing: '-0.02em',
                    fontSize: { xs: '1.15rem', sm: '1.4rem' },
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  User Profile
                </Typography>
                <Typography sx={{ color: INK_FAINT, fontWeight: 500, fontSize: '0.8rem' }}>
                  Account #{userId || '—'}
                </Typography>
              </Box>
            </Stack>

            <Chip
              label={`ID #${userId}`}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: '0.72rem',
                height: 27,
                bgcolor: `${BLUE}14`,
                color: BLUE,
                borderRadius: 8,
                display: { xs: 'none', sm: 'inline-flex' },
              }}
            />
          </Stack>

          {(() => {
            if (loading) {
              return (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={360}>
                  <CircularProgress size={34} thickness={4} sx={{ color: BLUE }} />
                </Box>
              );
            }
            if (!userInfo) {
              return (
                <Surface sx={{ p: { xs: 4, sm: 6 }, textAlign: 'center' }}>
                  <Typography sx={{ color: INK_FAINT, fontWeight: 500 }}>
                    No user profile details found for ID: {userId}.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/dashboard/user-management')}
                    sx={{
                      mt: 2.5,
                      borderRadius: 12,
                      bgcolor: BLUE,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      boxShadow: 'none',
                      '&:hover': { bgcolor: '#005BB5', boxShadow: 'none' },
                    }}
                  >
                    Back to User Management
                  </Button>
                </Surface>
              );
            }

            let statusColor = GREEN;
            let statusBg = 'rgba(52, 199, 89, 0.12)';
            if (st === 'inactive' || st === 'suspend' || st === 'block') {
              statusColor = RED;
              statusBg = 'rgba(255, 59, 48, 0.12)';
            } else if (st === 'pending') {
              statusColor = ORANGE;
              statusBg = 'rgba(255, 149, 0, 0.12)';
            }

            return (
              <Stack spacing={2.5}>
                {/* Identity + Quick Actions */}
                <Surface>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                    spacing={{ xs: 2.5, md: 2 }}
                  >
                    <Stack direction="row" spacing={2.25} alignItems="center" sx={{ minWidth: 0 }}>
                      <Avatar
                        sx={{
                          width: 68,
                          height: 68,
                          borderRadius: '20px',
                          background: `linear-gradient(135deg, ${BLUE} 0%, #5E5CE6 100%)`,
                          fontWeight: 700,
                          fontSize: '1.7rem',
                          color: '#FFFFFF',
                          flexShrink: 0,
                        }}
                      >
                        {displayName.charAt(0).toUpperCase()}
                      </Avatar>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: INK,
                            fontSize: '1.3rem',
                            letterSpacing: '-0.015em',
                            mb: 0.75,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {displayName}
                        </Typography>

                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.6 }}>
                          <StatusPill label={st.toUpperCase()} sx={{ bgcolor: statusBg, color: statusColor }} />
                          <StatusPill
                            label={isVip ? 'VIP Tier' : 'Standard'}
                            sx={{
                              bgcolor: isVip ? `${PURPLE}1F` : 'rgba(0,0,0,0.05)',
                              color: isVip ? PURPLE : INK_SOFT,
                            }}
                          />
                          <StatusPill
                            icon={
                              isKyc ? (
                                <CheckCircle style={{ fontSize: 13, color: GREEN }} />
                              ) : (
                                <Cancel style={{ fontSize: 13, color: RED }} />
                              )
                            }
                            label={isKyc ? 'KYC Verified' : 'KYC Pending'}
                            sx={{
                              bgcolor: isKyc ? `${GREEN}1A` : `${RED}1A`,
                              color: isKyc ? '#28A745' : '#D9251D',
                            }}
                          />
                          {hasPushToken && (
                            <StatusPill
                              icon={<NotificationsActive style={{ fontSize: 13, color: PURPLE }} />}
                              label="Push Registered"
                              sx={{ bgcolor: `${PURPLE}1A`, color: '#8E24AA' }}
                            />
                          )}
                        </Stack>
                      </Box>
                    </Stack>

                    {/* Quick actions */}
                    <Stack
                      direction="row"
                      sx={{
                        width: { xs: '100%', md: 'auto' },
                        maxWidth: { md: 340 },
                      }}
                    >
                      {quickActions.map(({ key, label, icon: Icon, tint, disabled, onClick, tooltip }) => (
                        <Box key={key} sx={{ flex: '1 1 0', minWidth: 0 }}>
                          <Tooltip title={tooltip}>
                            <span style={{ display: 'block' }}>
                              <QuickAction tint={tint} disabled={disabled} onClick={disabled ? undefined : onClick}>
                                <QuickActionIcon tint={tint}>
                                  <Icon sx={{ fontSize: 20 }} />
                                </QuickActionIcon>
                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: INK_SOFT }}>
                                  {label}
                                </Typography>
                              </QuickAction>
                            </span>
                          </Tooltip>
                        </Box>
                      ))}
                    </Stack>
                  </Stack>
                </Surface>

                {/* Wallet Overview */}
                <Surface>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1.5}
                    sx={{ mb: 2.5 }}
                  >
                    <SectionLabel>
                      <IconChip tint={GREEN}>
                        <AccountBalanceWallet sx={{ fontSize: 18 }} />
                      </IconChip>
                      <Typography sx={{ fontWeight: 700, color: INK, fontSize: '1.05rem', letterSpacing: '-0.01em' }}>
                        Wallet Overview
                      </Typography>
                    </SectionLabel>

                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!selectedUser || disableAction || !canFund}
                        onClick={() => toggleAddFund('Fund')}
                        sx={{
                          borderRadius: 10,
                          bgcolor: GREEN,
                          '&:hover': { bgcolor: '#2EA54A' },
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          px: 2,
                          boxShadow: 'none',
                        }}
                      >
                        Fund
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!selectedUser || disableAction || !canFund}
                        onClick={() => toggleDeductFund('Deduct')}
                        sx={{
                          borderRadius: 10,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          px: 2,
                          borderColor: 'rgba(255, 59, 48, 0.3)',
                          color: RED,
                          '&:hover': { borderColor: RED, bgcolor: 'rgba(255, 59, 48, 0.05)' },
                        }}
                      >
                        Deduct
                      </Button>
                    </Stack>
                  </Stack>

                  <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 2.5 }}>
                    {[
                      { label: 'Available Balance', value: userWallet?.availableBalance, tint: GREEN },
                      { label: 'Commission', value: userWallet?.commissions, tint: BLUE },
                      { label: 'Locked Balance', value: userWallet?.lockedBalance, tint: ORANGE },
                      { label: 'Ledger Balance', value: userWallet?.ledgerBalance, tint: PURPLE },
                    ].map((stat) => (
                      <Grid item xs={6} sm={3} key={stat.label}>
                        <StatTile tint={stat.tint}>
                          <Typography
                            sx={{
                              color: INK_FAINT,
                              fontWeight: 600,
                              display: 'block',
                              mb: 0.6,
                              fontSize: '0.68rem',
                              textTransform: 'uppercase',
                              letterSpacing: 0.4,
                            }}
                          >
                            {stat.label}
                          </Typography>
                          <Typography
                            sx={{ fontWeight: 700, color: INK, letterSpacing: '-0.02em', fontSize: '1.2rem' }}
                          >
                            ₦{fCurrency(stat.value || 0)}
                          </Typography>
                        </StatTile>
                      </Grid>
                    ))}
                  </Grid>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1.5}
                    sx={{ pt: 2, borderTop: `1px solid ${HAIRLINE}` }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.25}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ rowGap: 0.6 }}
                    >
                      <StatusPill
                        label={userWallet?.firstDeposit ? 'First Deposit Completed' : 'Pending Initial Deposit'}
                        sx={{
                          bgcolor: userWallet?.firstDeposit ? `${GREEN}1F` : `${ORANGE}1F`,
                          color: userWallet?.firstDeposit ? GREEN : ORANGE,
                        }}
                      />
                      <Typography sx={{ color: INK_FAINT, fontWeight: 500, fontSize: '0.78rem' }}>
                        {userWallet?.currency || 'NGN'} • Wallet #{userWallet?.id || '—'}
                      </Typography>
                    </Stack>

                    <Button
                      startIcon={<ReceiptLong sx={{ fontSize: 18 }} />}
                      endIcon={<ArrowForwardIos sx={{ fontSize: '11px !important' }} />}
                      onClick={navigateUserTransactionScreen}
                      disabled={!selectedUser}
                      fullWidth={false}
                      sx={{
                        borderRadius: 12,
                        bgcolor: INK,
                        color: '#FFFFFF',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 2.25,
                        py: 0.85,
                        fontSize: '0.8rem',
                        boxShadow: 'none',
                        '&:hover': { bgcolor: '#000000', boxShadow: 'none' },
                      }}
                    >
                      Transaction History
                    </Button>
                  </Stack>
                </Surface>

                {/* Profile Details + Push Token */}
                <Surface>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <SectionLabel>
                      <IconChip tint={BLUE}>
                        <Person sx={{ fontSize: 17 }} />
                      </IconChip>
                      <Typography sx={{ fontWeight: 700, color: INK, fontSize: '1rem', letterSpacing: '-0.01em' }}>
                        Profile Details
                      </Typography>
                    </SectionLabel>
                    <IconButton
                      size="small"
                      onClick={toggleModifyUser}
                      sx={{ bgcolor: 'rgba(0,0,0,0.04)', color: INK, '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' } }}
                    >
                      <Edit sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Stack>
                  <Divider sx={{ mb: 2, borderColor: HAIRLINE }} />

                  <Stack spacing={1.5} sx={{ mb: 3 }}>
                    <Tile>
                      <Typography
                        sx={{
                          color: INK_FAINT,
                          fontWeight: 600,
                          display: 'block',
                          mb: 0.3,
                          fontSize: '0.68rem',
                          textTransform: 'uppercase',
                        }}
                      >
                        Full Name
                      </Typography>
                      <Typography sx={{ fontWeight: 700, color: INK, fontSize: '0.92rem' }}>
                        {userInfo.firstName || ''} {userInfo.lastName || ''}
                      </Typography>
                    </Tile>

                    <Tile>
                      <Typography
                        sx={{
                          color: INK_FAINT,
                          fontWeight: 600,
                          display: 'block',
                          mb: 0.3,
                          fontSize: '0.68rem',
                          textTransform: 'uppercase',
                        }}
                      >
                        Username & Phone
                      </Typography>
                      <Typography sx={{ fontWeight: 700, color: BLUE, fontSize: '0.92rem' }}>
                        @{userInfo.username || '—'}
                      </Typography>
                      <Typography sx={{ color: INK_SOFT, fontWeight: 600, fontSize: '0.76rem' }}>
                        {userInfo.phone || '—'}
                      </Typography>
                    </Tile>

                    <Tile>
                      <Typography
                        sx={{
                          color: INK_FAINT,
                          fontWeight: 600,
                          display: 'block',
                          mb: 0.3,
                          fontSize: '0.68rem',
                          textTransform: 'uppercase',
                        }}
                      >
                        Email Address
                      </Typography>
                      <Typography sx={{ fontWeight: 700, color: INK, wordBreak: 'break-all', fontSize: '0.86rem' }}>
                        {userInfo.email || '—'}
                      </Typography>
                    </Tile>

                    <Grid container spacing={1.25}>
                      <Grid item xs={6}>
                        <Tile>
                          <Typography
                            sx={{
                              color: INK_FAINT,
                              fontWeight: 600,
                              display: 'block',
                              mb: 0.3,
                              fontSize: '0.65rem',
                              textTransform: 'uppercase',
                            }}
                          >
                            Tier
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Star sx={{ fontSize: 15, color: isVip ? PURPLE : INK_FAINT }} />
                            <Typography sx={{ fontWeight: 700, color: isVip ? PURPLE : INK_SOFT, fontSize: '0.82rem' }}>
                              {isVip ? 'VIP' : 'Standard'}
                            </Typography>
                          </Stack>
                        </Tile>
                      </Grid>

                      <Grid item xs={6}>
                        <Tile>
                          <Typography
                            sx={{
                              color: INK_FAINT,
                              fontWeight: 600,
                              display: 'block',
                              mb: 0.3,
                              fontSize: '0.65rem',
                              textTransform: 'uppercase',
                            }}
                          >
                            KYC
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <VerifiedUser sx={{ fontSize: 15, color: isKyc ? GREEN : RED }} />
                            <Typography sx={{ fontWeight: 700, color: isKyc ? GREEN : RED, fontSize: '0.82rem' }}>
                              {isKyc ? 'Verified' : 'Pending'}
                            </Typography>
                          </Stack>
                        </Tile>
                      </Grid>
                    </Grid>

                    <Tile>
                      <Typography
                        sx={{
                          color: INK_FAINT,
                          fontWeight: 600,
                          display: 'block',
                          mb: 0.5,
                          fontSize: '0.68rem',
                          textTransform: 'uppercase',
                        }}
                      >
                        Registration & Referral
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <CalendarToday sx={{ fontSize: 13, color: INK_FAINT }} />
                        <Typography sx={{ color: INK, fontWeight: 600, fontSize: '0.75rem' }}>
                          {userInfo.createdAt ? moment(userInfo.createdAt).format('DD MMM YYYY, hh:mm A') : '—'}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AccessTime sx={{ fontSize: 13, color: INK_FAINT }} />
                        <Typography sx={{ color: INK_SOFT, fontSize: '0.75rem' }}>
                          Referred by <strong>{userInfo.refer_by || 'Direct'}</strong>
                        </Typography>
                      </Stack>
                    </Tile>
                  </Stack>

                  <SectionLabel sx={{ mb: 2 }}>
                    <IconChip tint={PURPLE}>
                      <NotificationsActive sx={{ fontSize: 17 }} />
                    </IconChip>
                    <Typography sx={{ fontWeight: 700, color: INK, fontSize: '1rem', letterSpacing: '-0.01em' }}>
                      Push Token & Attribution
                    </Typography>
                  </SectionLabel>
                  <Divider sx={{ mb: 2, borderColor: HAIRLINE }} />

                  <Stack spacing={1.5}>
                    {hasPushToken ? (
                      <Box sx={{ p: 1.75, bgcolor: `${PURPLE}0F`, borderRadius: 14, border: `1px solid ${PURPLE}26` }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography
                            sx={{ color: PURPLE, fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase' }}
                          >
                            FCM Token Registered
                          </Typography>
                          <Chip
                            label="Active"
                            size="small"
                            sx={{
                              bgcolor: PURPLE,
                              color: '#FFFFFF',
                              fontWeight: 700,
                              height: 18,
                              fontSize: '0.6rem',
                              borderRadius: 6,
                            }}
                          />
                        </Stack>
                        <Typography
                          sx={{
                            display: 'block',
                            wordBreak: 'break-all',
                            fontFamily: 'monospace',
                            color: INK,
                            fontSize: '0.7rem',
                            lineHeight: 1.4,
                          }}
                        >
                          {userInfo.deviceToken}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ p: 1.75, bgcolor: 'rgba(0,0,0,0.025)', borderRadius: 14, textAlign: 'center' }}>
                        <Typography sx={{ color: INK_FAINT, fontWeight: 500, fontSize: '0.82rem' }}>
                          No FCM push token registered on user device.
                        </Typography>
                      </Box>
                    )}

                    <Tile>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.8 }}>
                        <Campaign sx={{ fontSize: 15, color: ORANGE }} />
                        <Typography
                          sx={{ color: INK_FAINT, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem' }}
                        >
                          Acquisition Attribution
                        </Typography>
                      </Stack>
                      <Typography sx={{ display: 'block', color: INK, fontWeight: 600, fontSize: '0.8rem' }}>
                        {[userInfo.utmSource, userInfo.utmMedium].filter(Boolean).join(' / ') || 'Direct Organic'}
                      </Typography>
                      <Typography sx={{ display: 'block', color: INK_SOFT, fontSize: '0.76rem' }}>
                        Campaign: {userInfo.utmCampaign || userInfo.campaignId || 'None'}
                      </Typography>
                    </Tile>

                    <Tile>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Key sx={{ fontSize: 15, color: INK_SOFT }} />
                        <Typography
                          sx={{ color: INK_FAINT, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem' }}
                        >
                          Access Token Key
                        </Typography>
                      </Stack>
                      <Typography
                        sx={{
                          fontFamily: 'monospace',
                          fontWeight: 600,
                          color: INK,
                          wordBreak: 'break-all',
                          fontSize: '0.72rem',
                        }}
                      >
                        {userInfo.accessTokenId || 'No active session token'}
                      </Typography>
                    </Tile>
                  </Stack>
                </Surface>

                {/* Active Sessions */}
                <Surface>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.25 }}>
                    <SectionLabel>
                      <IconChip tint={BLUE}>
                        <Devices sx={{ fontSize: 18 }} />
                      </IconChip>
                      <Typography sx={{ fontWeight: 700, color: INK, fontSize: '1.05rem', letterSpacing: '-0.01em' }}>
                        Active Sessions
                      </Typography>
                    </SectionLabel>
                    <Chip
                      label={`${activeSessions.length} ${activeSessions.length === 1 ? 'Session' : 'Sessions'}`}
                      size="small"
                      sx={{ fontWeight: 700, bgcolor: `${BLUE}14`, color: BLUE, borderRadius: 8 }}
                    />
                  </Stack>
                  <Divider sx={{ mb: 2, borderColor: HAIRLINE }} />

                  {activeSessions.length === 0 ? (
                    <Typography sx={{ color: INK_FAINT, py: 3, textAlign: 'center', fontWeight: 500 }}>
                      No active device sessions found for this user.
                    </Typography>
                  ) : (
                    <>
                      {/* Desktop / tablet: table view */}
                      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    color: INK_FAINT,
                                    py: 1.25,
                                    fontSize: '0.7rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.4,
                                    borderColor: HAIRLINE,
                                  }}
                                >
                                  Device
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    color: INK_FAINT,
                                    py: 1.25,
                                    fontSize: '0.7rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.4,
                                    borderColor: HAIRLINE,
                                  }}
                                >
                                  OS & App
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    color: INK_FAINT,
                                    py: 1.25,
                                    fontSize: '0.7rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.4,
                                    borderColor: HAIRLINE,
                                  }}
                                >
                                  IP Address
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    color: INK_FAINT,
                                    py: 1.25,
                                    fontSize: '0.7rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.4,
                                    borderColor: HAIRLINE,
                                  }}
                                >
                                  Last Active
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    fontWeight: 700,
                                    color: INK_FAINT,
                                    py: 1.25,
                                    fontSize: '0.7rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.4,
                                    borderColor: HAIRLINE,
                                  }}
                                >
                                  Session
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {activeSessions.map((session, index) => {
                                const { isMobileSession, deviceLabel, osLabel, browserLabel } = getSessionMeta(session);

                                return (
                                  <TableRow
                                    key={session.sessionId || index}
                                    hover
                                    onClick={() => setSelectedSession(session)}
                                    sx={{ cursor: 'pointer', '&:last-child td, &:last-child th': { border: 0 } }}
                                  >
                                    <TableCell sx={{ py: 1.5, borderColor: HAIRLINE }}>
                                      <Stack direction="row" spacing={1.25} alignItems="center">
                                        <Avatar
                                          sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: 9,
                                            bgcolor: isMobileSession ? `${GREEN}1F` : `${BLUE}1F`,
                                            color: isMobileSession ? GREEN : BLUE,
                                          }}
                                        >
                                          {isMobileSession ? (
                                            <Smartphone style={{ fontSize: 16 }} />
                                          ) : (
                                            <Computer style={{ fontSize: 16 }} />
                                          )}
                                        </Avatar>
                                        <Box>
                                          <Typography sx={{ fontWeight: 700, color: INK, fontSize: '0.83rem' }}>
                                            {deviceLabel}
                                          </Typography>
                                          {session.deviceId && (
                                            <Typography
                                              sx={{ color: INK_FAINT, fontFamily: 'monospace', fontSize: '0.66rem' }}
                                            >
                                              {session.deviceId.slice(0, 16)}...
                                            </Typography>
                                          )}
                                        </Box>
                                      </Stack>
                                    </TableCell>

                                    <TableCell sx={{ py: 1.5, borderColor: HAIRLINE }}>
                                      <Typography sx={{ fontWeight: 700, color: INK, fontSize: '0.8rem' }}>
                                        {osLabel}
                                      </Typography>
                                      <Typography sx={{ color: INK_FAINT, fontSize: '0.72rem' }}>
                                        {browserLabel}
                                      </Typography>
                                    </TableCell>

                                    <TableCell sx={{ py: 1.5, borderColor: HAIRLINE }}>
                                      <Typography
                                        sx={{
                                          fontFamily: 'monospace',
                                          color: BLUE,
                                          fontSize: '0.78rem',
                                          fontWeight: 600,
                                        }}
                                      >
                                        {session.ipAddress || '—'}
                                      </Typography>
                                    </TableCell>

                                    <TableCell sx={{ py: 1.5, borderColor: HAIRLINE }}>
                                      <Typography sx={{ color: INK, fontSize: '0.8rem', fontWeight: 700 }}>
                                        {session.lastActiveAt
                                          ? moment(session.lastActiveAt).format('DD MMM YYYY, hh:mm A')
                                          : '—'}
                                      </Typography>
                                      <Typography sx={{ color: INK_FAINT, fontSize: '0.7rem' }}>
                                        {session.lastActiveAt ? moment(session.lastActiveAt).fromNow() : ''}
                                      </Typography>
                                    </TableCell>

                                    <TableCell align="right" sx={{ py: 1.5, borderColor: HAIRLINE }}>
                                      <Tooltip title={session.sessionId || ''}>
                                        <Chip
                                          label={session.sessionId ? `${session.sessionId.slice(0, 8)}...` : '—'}
                                          size="small"
                                          sx={{
                                            fontWeight: 600,
                                            fontFamily: 'monospace',
                                            fontSize: '0.66rem',
                                            bgcolor: 'rgba(0,0,0,0.05)',
                                            color: INK,
                                            borderRadius: 6,
                                          }}
                                        />
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>

                      {/* Mobile: tappable card list */}
                      <Stack spacing={1.25} sx={{ display: { xs: 'flex', sm: 'none' } }}>
                        {activeSessions.map((session, index) => {
                          const { isMobileSession, deviceLabel, osLabel } = getSessionMeta(session);

                          return (
                            <Box
                              key={session.sessionId || index}
                              onClick={() => setSelectedSession(session)}
                              role="button"
                              tabIndex={0}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.25,
                                p: 1.5,
                                borderRadius: 16,
                                border: `1px solid ${HAIRLINE}`,
                                cursor: 'pointer',
                                transition: 'background-color 0.15s ease, transform 0.15s ease',
                                '&:active': { backgroundColor: 'rgba(0,0,0,0.03)', transform: 'scale(0.99)' },
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 38,
                                  height: 38,
                                  borderRadius: 11,
                                  bgcolor: isMobileSession ? `${GREEN}1F` : `${BLUE}1F`,
                                  color: isMobileSession ? GREEN : BLUE,
                                  flexShrink: 0,
                                }}
                              >
                                {isMobileSession ? (
                                  <Smartphone style={{ fontSize: 18 }} />
                                ) : (
                                  <Computer style={{ fontSize: 18 }} />
                                )}
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    color: INK,
                                    fontSize: '0.85rem',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {deviceLabel}
                                </Typography>
                                <Typography
                                  sx={{
                                    color: INK_SOFT,
                                    fontSize: '0.74rem',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {osLabel} • {session.ipAddress || '—'}
                                </Typography>
                                <Typography sx={{ color: INK_FAINT, fontSize: '0.7rem' }}>
                                  {session.lastActiveAt ? moment(session.lastActiveAt).fromNow() : '—'}
                                </Typography>
                              </Box>
                              <ArrowForwardIos sx={{ fontSize: 13, color: INK_FAINT, flexShrink: 0 }} />
                            </Box>
                          );
                        })}
                      </Stack>
                    </>
                  )}
                </Surface>
              </Stack>
            );
          })()}
        </Container>
      </Box>
    </Page>
  );
}
